<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue'
import { Hand, MousePointer2, Plus } from 'lucide-vue-next'
import BuffIcon from './BuffIcon.vue'
import type { BuffDisplayItem, BuffSnapshot } from '@/buffs/types'
import { buffMessages } from '@/i18n/buffs'
import { buffGridLayout } from '@/buffs/layout'
import { buffItemPresentation, buffRailStatus } from '@/buffs/presentation'
import '@/buffs/strip.css'
const props = defineProps<{ snapshot: BuffSnapshot; editable?: boolean; compact?: boolean; iconSize?: number; reorder?: (id: string, index: number) => Promise<void> }>()
const emit = defineEmits<{ add: [anchor: HTMLElement]; edit: [id: string, anchor: HTMLElement]; error: [message: string]; extraHeight: [height: number] }>()
const root = ref<HTMLElement>(), list = ref<HTMLElement>()
const width = ref(320), error = ref(''), saving = ref(false)
const draft = ref<string[] | null>(null), dragId = ref(''), lockedSize = ref(0)
const ghostNode = ref<HTMLElement>()
const ghost = { x: 0, y: 0, dx: 0, dy: 0 }
const copy = computed(() => buffMessages[props.snapshot.locale])
const railStatus = computed(() => buffRailStatus(props.snapshot))
const items = computed(() => draft.value ? draft.value.map(id => props.snapshot.items.find(item => item.id === id)).filter((item): item is BuffDisplayItem => !!item) : props.snapshot.items)
const grid = computed(() => buffGridLayout(width.value, items.value.length + (props.editable ? 1 : 0), props.editable ? 36 : props.iconSize ?? 28, props.editable ? 8 : 4, 0, props.compact ? 2 : Infinity))
const size = computed(() => lockedSize.value || grid.value.size)
watch(() => Math.max(0, grid.value.rows - 1) * (grid.value.size + grid.value.gap), height => emit('extraHeight', height), { immediate: true })
const dragged = computed(() => props.snapshot.items.find(item => item.id === dragId.value))
const presentations = computed(() => new Map(props.snapshot.items.map(item => [item.id, buffItemPresentation(item, props.snapshot)])))
const display = (item: BuffDisplayItem) => presentations.value.get(item.id)!
const tooltipId = useId(), tooltipTarget = ref<HTMLElement>(), tooltipNode = ref<HTMLElement>()
const tooltip = ref<{ id: string; x: number; y: number } | null>(null)
const tooltipText = computed(() => {
  const item = props.snapshot.items.find(item => item.id === tooltip.value?.id)
  return item ? `${item.name} · ${display(item).label}` : null
})
async function hint(event: Event, id: string) {
  if (dragId.value || saving.value) return
  const node = event.currentTarget as HTMLElement, view = node.ownerDocument.defaultView
  if (!view) return
  const rect = node.getBoundingClientRect()
  tooltip.value = { id, x: rect.left + rect.width / 2, y: rect.bottom + 6 }
  const current = tooltip.value
  await nextTick()
  if (tooltip.value !== current || !tooltipNode.value) return
  const half = tooltipNode.value.offsetWidth / 2, height = tooltipNode.value.offsetHeight
  current.x = Math.max(half + 8, Math.min(view.innerWidth - half - 8, current.x))
  current.y = rect.bottom + height + 14 <= view.innerHeight ? rect.bottom + 6 : Math.max(8, rect.top - height - 6)
}
let observer: ResizeObserver | undefined, hold: ReturnType<typeof setTimeout> | undefined
let pointer: { id: string; pointerId: number; x: number; y: number; touch: boolean; element: HTMLElement } | null = null
let suppressClickUntil = 0, disposed = false
let activeDocument: Document | undefined
let slots: DOMRect[] = [], slotScroll = 0, orderRevision = 0
let frame = 0, pendingPoint: { x: number; y: number } | null = null, listBox: DOMRect | null = null
const animations = new Map<HTMLElement, Animation>()
const reducedMotion = () => !!list.value?.ownerDocument.defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)').matches
function cancelAnimations() { for (const animation of animations.values()) animation.cancel(); animations.clear() }
function measure() {
  if (list.value && !dragId.value) {
    const style = list.value.ownerDocument.defaultView?.getComputedStyle(list.value)
    const inset = (parseFloat(style?.paddingLeft ?? '') || 0) + (parseFloat(style?.paddingRight ?? '') || 0)
    width.value = Math.max(0, (list.value.clientWidth || 320) - inset)
  }
}
function resized() {
  if (dragId.value && listBox && list.value) {
    const box = list.value.getBoundingClientRect()
    if (box.width !== listBox.width || box.height !== listBox.height) cancel()
  }
  tooltip.value = null
  measure()
}
function nodes() { return [...(list.value?.querySelectorAll<HTMLElement>('[data-buff-id]') ?? [])] }
function focus(id: string) { nodes().find(node => node.dataset.buffId === id)?.focus({ preventScroll: true }) }
defineExpose({ focus, focusAdd: () => list.value?.querySelector<HTMLButtonElement>('.buff-add')?.focus() })
function click(item: BuffDisplayItem, event: MouseEvent) {
  if (Date.now() < suppressClickUntil || saving.value) return
  if (props.editable) emit('edit', item.id, event.currentTarget as HTMLElement)
}
async function animateOrder(ids: string[]) {
  const revision = ++orderRevision
  // Sample the currently painted positions, then replace (never stack) animations.
  const before = new Map(nodes().map(node => [node.dataset.buffId, node.getBoundingClientRect()]))
  cancelAnimations()
  draft.value = ids
  await nextTick()
  if (revision !== orderRevision || disposed) return
  if (reducedMotion()) return
  // Batch all destination reads before starting animations.
  const destinations = nodes().map(node => ({ node, rect: node.getBoundingClientRect() }))
  for (const { node, rect: current } of destinations) {
    const previous = before.get(node.dataset.buffId)
    if (!previous || node.dataset.buffId === dragId.value || !node.animate || (previous.left === current.left && previous.top === current.top)) continue
    const animation = node.animate([
      { transform: `translate(${previous.left - current.left}px, ${previous.top - current.top}px)` }, { transform: 'translate(0,0)' },
    ], { duration: 280, easing: 'cubic-bezier(.25,.46,.45,.94)' })
    animations.set(node, animation)
    void animation.finished.then(() => { if (animations.get(node) === animation) animations.delete(node) }, () => {})
  }
}
function begin() {
  if (!pointer || !props.reorder) return
  cancelAnimations()
  const bounds = pointer.element.getBoundingClientRect()
  // Hit testing uses stationary layout slots, not icons moving through a FLIP animation.
  slots = nodes().map(node => node.getBoundingClientRect()); slotScroll = list.value!.scrollTop
  listBox = list.value!.getBoundingClientRect()
  lockedSize.value = size.value
  draft.value = props.snapshot.items.map(item => item.id)
  dragId.value = pointer.id
  Object.assign(ghost, { x: bounds.left, y: bounds.top, dx: pointer.x - bounds.left, dy: pointer.y - bounds.top })
  void nextTick(paintGhost)
  list.value?.setPointerCapture?.(pointer.pointerId)
}
function down(event: PointerEvent, item: BuffDisplayItem) {
  if (!props.reorder || saving.value || event.button !== 0 || pointer) return
  error.value = ''
  tooltip.value = null
  pointer = { id: item.id, pointerId: event.pointerId, x: event.clientX, y: event.clientY, touch: event.pointerType === 'touch', element: event.currentTarget as HTMLElement }
  activeDocument = list.value!.ownerDocument
  activeDocument.addEventListener('pointermove', move, { passive: false })
  activeDocument.addEventListener('pointerup', up)
  activeDocument.addEventListener('pointercancel', cancel)
  activeDocument.addEventListener('touchmove', touchMove, { passive: false })
  activeDocument.addEventListener('keydown', escape)
  activeDocument.defaultView?.addEventListener('blur', cancel)
  activeDocument.defaultView?.addEventListener('resize', cancel)
  activeDocument.addEventListener('scroll', scrolled, true)
  if (pointer.touch) hold = setTimeout(begin, 280)
}
function touchMove(event: TouchEvent) { if (dragId.value) event.preventDefault() }
function move(event: PointerEvent) {
  if (!pointer || event.pointerId !== pointer.pointerId) return
  const distance = Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y)
  if (!dragId.value && distance > 6) {
    if (pointer.touch) { cancel(); return }
    begin()
  }
  if (!dragId.value) return
  event.preventDefault()
  pendingPoint = { x: event.clientX, y: event.clientY }
  if (!frame) frame = activeDocument!.defaultView!.requestAnimationFrame(dragFrame)
}
function paintGhost() {
  if (ghostNode.value) ghostNode.value.style.transform = `translate3d(${ghost.x}px,${ghost.y}px,0)`
}
function dragFrame() {
  frame = 0
  if (!pendingPoint || !dragId.value || !list.value || !listBox) return
  const point = pendingPoint
  ghost.x = point.x - ghost.dx; ghost.y = point.y - ghost.dy
  const scroll = list.value.scrollTop
  const y = point.y + scroll - slotScroll
  const inset = 3
  const to = slots.findIndex(rect => point.x >= rect.left + inset && point.x <= rect.right - inset && y >= rect.top + inset && y <= rect.bottom - inset)
  const from = draft.value!.indexOf(dragId.value)
  if (to >= 0 && to !== from) {
    const order = [...draft.value!]
    order.splice(to, 0, order.splice(from, 1)[0]!)
    void animateOrder(order)
  }
  paintGhost()
  // Continue edge scrolling even when the pointer stops; don't scale speed with input frequency.
  if (point.y > listBox.bottom - 24) list.value.scrollTop += 6
  else if (point.y < listBox.top + 24) list.value.scrollTop -= 6
  if (list.value.scrollTop !== scroll) frame = activeDocument!.defaultView!.requestAnimationFrame(dragFrame)
}
function scrolled(event: Event) {
  // Own scrolling is included in hit tests; an ancestor scroll invalidates cached geometry.
  if (event.target !== list.value) cancel()
}
function release() {
  clearTimeout(hold)
  if (frame) activeDocument?.defaultView?.cancelAnimationFrame(frame)
  frame = 0; pendingPoint = null; listBox = null
  if (pointer && list.value?.hasPointerCapture?.(pointer.pointerId)) list.value.releasePointerCapture(pointer.pointerId)
  activeDocument?.removeEventListener('pointermove', move)
  activeDocument?.removeEventListener('pointerup', up)
  activeDocument?.removeEventListener('pointercancel', cancel)
  activeDocument?.removeEventListener('touchmove', touchMove)
  activeDocument?.removeEventListener('keydown', escape)
  activeDocument?.defaultView?.removeEventListener('blur', cancel)
  activeDocument?.defaultView?.removeEventListener('resize', cancel)
  activeDocument?.removeEventListener('scroll', scrolled, true)
  activeDocument = undefined; pointer = null
}
function cancel() { if (dragId.value) suppressClickUntil = Date.now() + 350; release(); orderRevision++; cancelAnimations(); slots = []; dragId.value = ''; draft.value = null; lockedSize.value = 0 }
function escape(event: KeyboardEvent) { if (event.key === 'Escape') { event.preventDefault(); cancel() } }
async function persist(id: string, to: number) {
  saving.value = true; error.value = ''
  try { await props.reorder?.(id, to) }
  catch { error.value = copy.value.saveError; emit('error', error.value) }
  finally { saving.value = false; draft.value = null; lockedSize.value = 0; slots = []; await nextTick(); if (!disposed) { focus(id); measure() } }
}
async function up(event: PointerEvent) {
  if (!pointer || event.pointerId !== pointer.pointerId) return
  if (frame) activeDocument?.defaultView?.cancelAnimationFrame(frame)
  // Flush the last move when release arrives before the next paint.
  dragFrame()
  const id = dragId.value, to = draft.value?.indexOf(id) ?? -1
  release()
  if (!id) return
  saving.value = true
  suppressClickUntil = Date.now() + 350
  const revision = orderRevision
  await nextTick()
  if (disposed || revision !== orderRevision) { saving.value = false; return }
  const node = ghostNode.value
  const destination = nodes().find(node => node.dataset.buffId === id)?.getBoundingClientRect()
  if (node && destination && node.animate && !reducedMotion()) {
    saving.value = true
    const animation = node.animate([{ transform: `translate3d(${ghost.x}px,${ghost.y}px,0)` }, { transform: `translate3d(${destination.left}px,${destination.top}px,0)` }], { duration: 240, easing: 'cubic-bezier(.25,.46,.45,.94)', fill: 'forwards' })
    animations.set(node, animation)
    try { await animation.finished } catch { /* Cancelled or window closed. */ }
    animations.delete(node)
  }
  if (disposed || revision !== orderRevision) { saving.value = false; return }
  dragId.value = ''
  if (!disposed) await persist(id, to)
}
async function key(event: KeyboardEvent, item: BuffDisplayItem) {
  if (!props.reorder || saving.value || !event.altKey || !['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(event.key)) return
  event.preventDefault()
  const index = items.value.findIndex(value => value.id === item.id)
  const step = event.key === 'ArrowUp' ? -grid.value.columns : event.key === 'ArrowDown' ? grid.value.columns : event.key === 'ArrowLeft' ? -1 : 1
  const to = Math.max(0, Math.min(items.value.length - 1, index + step))
  if (to === index) return
  saving.value = true
  const order = items.value.map(value => value.id); order.splice(to, 0, order.splice(index, 1)[0]!)
  await animateOrder(order); await persist(item.id, to)
}
onMounted(() => { tooltipTarget.value = root.value?.ownerDocument.body; if (typeof ResizeObserver !== 'undefined') { observer = new ResizeObserver(resized); if (list.value) observer.observe(list.value); if (root.value) observer.observe(root.value) }; measure() })
watch(() => props.snapshot.items.map(item => item.id).sort().join('|'), () => { if (dragId.value) cancel(); void nextTick(measure) })
watch(() => props.iconSize, () => { void nextTick(measure) })
onBeforeUnmount(() => { disposed = true; orderRevision++; cancelAnimations(); release(); observer?.disconnect() })
</script>
<template>
  <section ref="root" class="buff-strip" :class="{ 'buff-strip-editable': editable, 'buff-strip-compact': compact }" :style="{ '--buff-icon': `${size}px`, '--buff-gap': `${grid.gap}px` }" aria-label="BUFF">
    <div class="buff-rail-content">
    <div ref="list" class="buff-items" :class="{ 'is-sorting': dragId }">
      <button v-for="item in items" :key="item.id" type="button" class="buff-item" :data-buff-id="item.id"
        :class="{ 'buff-missing': display(item).presence === 'missing', 'buff-disabled': display(item).disabled, 'buff-near': display(item).nearing, 'buff-drag-placeholder': dragId === item.id }"
        :aria-label="`${item.name} · ${display(item).label}`" :aria-describedby="tooltip?.id === item.id ? tooltipId : undefined"
        @pointerenter="hint($event, item.id)" @focus="hint($event, item.id)" @pointerleave="tooltip = null" @blur="tooltip = null" @keydown.esc="tooltip = null"
        @pointerdown="down($event, item)" @click="click(item, $event)" @keydown="key($event, item)" @dragstart.prevent>
        <BuffIcon :item="item" :snapshot="snapshot" :size="size" />
        <span class="buff-sr-only">{{ display(item).label }}</span>
      </button>
      <button v-if="editable" class="buff-item buff-add" :class="{ 'buff-add-empty': !items.length }" type="button" :aria-label="copy.add" @click="emit('add', $event.currentTarget as HTMLElement)"><span class="buff-add-box"><Plus :size="20" aria-hidden="true" /></span><span v-if="!items.length">{{ copy.add }}</span></button>
    </div>
    </div>
    <span class="buff-sr-only" role="status" aria-live="polite" aria-atomic="true">{{ items.length ? railStatus : '' }}</span>
    <span v-if="error" class="buff-sr-only" role="alert">{{ error }}</span>
    <div v-if="dragged" ref="ghostNode" class="buff-drag-ghost" aria-hidden="true"><BuffIcon :item="dragged" :snapshot="snapshot" :size="size" /></div>
    <Teleport v-if="tooltipTarget" :to="tooltipTarget">
      <Transition name="buff-hint" :duration="{ enter: 180, leave: 120 }">
        <span v-if="tooltip && tooltipText" :id="tooltipId" ref="tooltipNode" role="tooltip" class="buff-tooltip" :style="{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }">
          <span class="buff-tooltip-summary">{{ tooltipText }}</span>
          <span v-if="editable" class="buff-tooltip-actions">
            <span><MousePointer2 :size="12" aria-hidden="true" />{{ copy.clickSettings }}</span>
            <span v-if="reorder"><Hand :size="12" aria-hidden="true" />{{ copy.tooltipDrag }}</span>
          </span>
        </span>
      </Transition>
    </Teleport>
  </section>
</template>
